import { useId, useMemo } from "react";

const CHART_WIDTH = 333;
const CHART_HEIGHT = 58;
const PADDING = 2; // Prevents 2px stroke clipping at top/bottom edges

const LineChart = ({ data = [], color = "#4AB932" }) => {
  const gradientId = useId();

  const { linePath, areaPath } = useMemo(() => {
    if (!data || data.length < 2) {
      return { linePath: "", areaPath: "" };
    }

    // Single-pass value extraction and min/max calculation
    let min = Infinity;
    let max = -Infinity;

    const values = new Array(data.length);
    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      const val = typeof item === "number" ? item : (item?.[1] ?? 0);
      values[i] = val;
      if (val < min) min = val;
      if (val > max) max = val;
    }

    const range = max - min || 1;
    const usableHeight = CHART_HEIGHT - PADDING * 2;
    const lenMinusOne = values.length - 1;

    // Calculate normalized point coordinates
    const points = values.map((val, i) => ({
      x: (i / lenMinusOne) * CHART_WIDTH,
      y: CHART_HEIGHT - PADDING - ((val - min) / range) * usableHeight,
    }));

    // Generate smooth line path using quadratic Bézier curves
    let line = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;

    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const xc = (prev.x + curr.x) / 2;
      const yc = (prev.y + curr.y) / 2;

      line += ` Q ${prev.x.toFixed(1)} ${prev.y.toFixed(1)}, ${xc.toFixed(1)} ${yc.toFixed(1)}`;
    }

    const last = points[points.length - 1];
    line += ` T ${last.x.toFixed(1)} ${last.y.toFixed(1)}`;

    const area = `${line} L ${CHART_WIDTH} ${CHART_HEIGHT} L 0 ${CHART_HEIGHT} Z`;

    return { linePath: line, areaPath: area };
  }, [data]);

  if (!linePath) return null;

  return (
    <div
      className="pointer-events-none absolute bottom-3 left-6 right-6 h-[85px]"
      aria-hidden="true"
    >
      <svg
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        preserveAspectRatio="none"
        className="absolute bottom-0 left-0 h-[63px] w-full"
      >
        <defs>
          <linearGradient
            id={gradientId}
            x1="0"
            y1="0"
            x2="0"
            y2={CHART_HEIGHT}
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        <path d={areaPath} fill={`url(#${gradientId})`} />

        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

export default LineChart;